import { useState, useRef, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, BookOpen, ArrowUpRight } from "lucide-react";
import { JOURNEY, JOURNEY_META } from "../data/journey";

const ROUTE = JOURNEY.map((s) => [s.lat, s.lng]);
const TILE_URL = `https://api.maptiler.com/maps/toner-v2/{z}/{x}/{y}.png?key=${process.env.REACT_APP_MAPTILER_KEY}`;

function iconHtml(order, active) {
  return L.divIcon({
    className: "",
    html: `<div class="hanko-marker ${active ? "hanko-marker--active" : ""}">${order}</div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
    popupAnchor: [0, -16],
  });
}

function popupHtml(s) {
  return `
    <div style="min-width:190px">
      <p style="font-size:11px;letter-spacing:1px;text-transform:uppercase;color:#7B8D62;margin:0">${s.when}</p>
      <p style="font-family:'Cormorant Garamond',serif;font-size:22px;margin:2px 0 6px">${s.place} <span style="color:#E03C31">${s.jp}</span></p>
      <p style="font-size:13px;color:#555;margin:0">${s.desc}</p>
      ${s.haiku ? `<p style="font-style:italic;font-family:'Cormorant Garamond',serif;white-space:pre-line;margin-top:8px;font-size:15px">${s.haiku.en}</p>` : ""}
    </div>`;
}

export default function BashoMap() {
  const [active, setActive] = useState(null);
  const [searchParams] = useSearchParams();
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef({});

  // Build the Leaflet map once, imperatively (no react-leaflet).
  useEffect(() => {
    if (mapRef.current || !mapEl.current) return;
    const map = L.map(mapEl.current, {
      center: [37.6, 139.2],
      zoom: 6,
      scrollWheelZoom: false,
    });
    mapRef.current = map;

    L.tileLayer(TILE_URL, {
      attribution: "&copy; MapTiler &copy; OpenStreetMap contributors",
      maxZoom: 20,
    }).addTo(map);

    L.polyline(ROUTE, {
      color: "#E03C31", weight: 2.5, opacity: 0.85, dashArray: "1 8", lineCap: "round",
    }).addTo(map);

    JOURNEY.forEach((s) => {
      const marker = L.marker([s.lat, s.lng], { icon: iconHtml(s.order, false) })
        .addTo(map)
        .bindPopup(popupHtml(s));
      marker.on("click", () => setActive(s));
      markersRef.current[s.id] = marker;
    });

    return () => {
      map.remove();
      mapRef.current = null;
      markersRef.current = {};
    };
  }, []);

  // Deep-link: /map?stop=<id> pre-selects a stop.
  useEffect(() => {
    const id = searchParams.get("stop");
    if (id) {
      const stop = JOURNEY.find((s) => s.id === id);
      if (stop) setActive(stop);
    }
  }, [searchParams]);

  // Reflect the active stop: fly there, refresh marker icons, open popup.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    JOURNEY.forEach((s) => {
      const m = markersRef.current[s.id];
      if (m) m.setIcon(iconHtml(s.order, active?.id === s.id));
    });
    if (active) {
      map.flyTo([active.lat, active.lng], 8, { duration: 1.1 });
      const m = markersRef.current[active.id];
      if (m) setTimeout(() => m.openPopup(), 500);
    }
  }, [active]);

  return (
    <div data-testid="map-page">
      {/* header */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-20 pb-10">
        <Link to="/basho" data-testid="back-to-life"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-8">
          <BookOpen className="w-4 h-4" /> Return to the Life of Bashō
        </Link>
        <span className="hanko text-primary text-2xl block mt-2">奥の細道</span>
        <h1 className="font-serif font-light text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tighter mt-3 leading-[0.95]">
          Bashō's Map of Travels
        </h1>
        <p className="mt-4 text-sm tracking-[0.25em] uppercase text-secondary">
          {JOURNEY_META.subtitle} · {JOURNEY_META.year}
        </p>
        <p className="mt-6 max-w-2xl text-lg text-muted-foreground leading-relaxed">
          {JOURNEY_META.summary}
        </p>
      </section>

      {/* map + itinerary */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-24 grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-7 order-2 lg:order-1">
          <div
            ref={mapEl}
            data-testid="leaflet-map"
            className="basho-map border border-border h-[560px] lg:h-[720px] lg:sticky lg:top-24"
            style={{ background: "#e9e3d6" }}
          />
        </div>

        <div className="lg:col-span-5 order-1 lg:order-2">
          <div className="border-t border-border">
            {JOURNEY.map((s) => {
              const isActive = active?.id === s.id;
              return (
                <div
                  key={s.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => setActive(s)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setActive(s); } }}
                  data-testid={`stop-${s.id}`}
                  className={`w-full text-left border-b border-border px-4 py-5 flex gap-4 transition-colors cursor-pointer outline-none ${
                    isActive ? "bg-primary/5 border-l-2 border-l-primary" : "hover:bg-muted/50 border-l-2 border-l-transparent"
                  }`}
                >
                  <span className={`hanko text-sm shrink-0 w-7 h-7 flex items-center justify-center border ${
                    isActive ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground"
                  }`}>
                    {s.order}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs tracking-widest uppercase text-secondary flex items-center gap-1.5">
                      <MapPin className="w-3 h-3" /> {s.when}
                    </p>
                    <h3 className="font-serif text-2xl mt-0.5">
                      {s.place} <span className="text-primary text-lg">{s.jp}</span>
                    </h3>
                    {s.kigo && (
                      <span className="inline-block mt-1.5 text-[11px] tracking-wide px-2 py-0.5 border border-secondary/40 text-secondary" data-testid={`kigo-${s.id}`}>
                        季語 · {s.kigo}
                      </span>
                    )}
                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{s.desc}</p>
                    {isActive && s.passage && (
                      <p className="text-sm text-foreground/80 mt-3 leading-relaxed animate-fade-up" data-testid={`passage-${s.id}`}>
                        {s.passage}
                      </p>
                    )}
                    {s.haiku && (
                      <p className="font-poem italic text-base text-foreground/80 whitespace-pre-line mt-3 border-l border-border pl-4">
                        {s.haiku.en}
                      </p>
                    )}
                    <Link to="/basho#life-1689" data-testid={`stop-life-${s.id}`}
                      onClick={(e) => e.stopPropagation()}
                      className="mt-3 inline-flex items-center gap-1.5 text-xs text-secondary hover:text-primary transition-colors">
                      <BookOpen className="w-3 h-3" /> In his life story <ArrowUpRight className="w-3 h-3" />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-muted-foreground mt-5 px-4">
            Tap a stop to trace the road on the map. Waypoints follow Bashō's historical itinerary.
          </p>
        </div>
      </section>
    </div>
  );
}
